/* eslint-disable react/prop-types */
import React, {useState, useEffect, useMemo} from 'react';
import {useMediaQuery} from 'react-responsive';
import {
  Button,
  Fab,
  Drawer,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Android,
  Apple,
  StorefrontOutlined,
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
} from '@mui/icons-material';
import Tab from '../../Components/tab';
import logo from '../../Assets/logo.png';
import Footer from '../../Components/Footer';
import {Link, useSearchParams} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile,
  signOut,
} from 'firebase/auth';

// ─── Backend API ─────────────────────────────────────────────────────────────
// TODO: update once the referral endpoint is deployed
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Animations ─────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(40px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SlideUp = styled.div`
  animation: ${slideUp} 0.6s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const CodeValue = styled.p`
  background: linear-gradient(90deg, #ff6600, #ff9040, #ff6600);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
`;

// ─── Styles ─────────────────────────────────────────────────────────────────

const colors = {
  azul: '#06448e',
  laranja: '#FF6600',
  roxo: '#7c53e3',
  bg: '#0a1628',
  card: '#ffffff',
  text: '#1a1a2e',
  muted: '#8a94a6',
  success: '#059669',
  surface: '#f4f6fa',
};

const s = {
  page: {
    overflowX: 'hidden',
    width: '100%',
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
  },
  hero: sm => ({
    background: `linear-gradient(145deg, ${colors.azul} 0%, ${colors.azul}  30%, ${colors.roxo}  70%,  ${colors.roxo} 100%)`,
    width: '100%',
    minHeight: sm ? 'auto' : 560,
    marginTop: 60,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: sm ? 40 : 80,
    paddingBottom: sm ? 50 : 80,
    position: 'relative',
    overflow: 'hidden',
  }),
  heroGlow: {
    position: 'absolute',
    top: -120,
    right: -120,
    width: 400,
    height: 400,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(255,102,0,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroGlow2: {
    position: 'absolute',
    bottom: -80,
    left: -80,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(124,83,227,0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  codeBox: sm => ({
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: sm ? '20px 24px' : '28px 40px',
    textAlign: 'center',
    maxWidth: 420,
    width: sm ? '90%' : 420,
    margin: '0 auto',
  }),
  codeLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
    margin: 0,
  },
  codeValue: sm => ({
    fontSize: sm ? 36 : 48,
    fontWeight: 800,
    letterSpacing: 3,
    margin: '8px 0 4px',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  }),
  copyBtn: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 10,
    marginTop: 8,
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '4px 16px',
  },
  title: sm => ({
    color: '#fff',
    fontSize: sm ? 28 : 42,
    fontWeight: 800,
    textAlign: 'center',
    maxWidth: 600,
    margin: '32px auto 12px',
    lineHeight: 1.15,
    letterSpacing: -0.5,
  }),
  subtitle: sm => ({
    color: 'rgba(255,255,255,0.6)',
    fontSize: sm ? 15 : 18,
    fontWeight: 400,
    textAlign: 'center',
    maxWidth: 480,
    margin: '0 auto 36px',
    lineHeight: 1.5,
  }),
  section: sm => ({
    maxWidth: 800,
    margin: '0 auto',
    padding: sm ? '40px 16px' : '60px 24px',
  }),
  sectionTitle: sm => ({
    fontSize: sm ? 24 : 32,
    fontWeight: 800,
    color: colors.text,
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: -0.3,
  }),
  sectionSub: {
    fontSize: 15,
    color: colors.muted,
    textAlign: 'center',
    marginBottom: 36,
    maxWidth: 500,
    margin: '0 auto 36px',
  },
  intentGrid: sm => ({
    display: 'grid',
    gridTemplateColumns: sm ? '1fr' : 'repeat(3, 1fr)',
    gap: sm ? 14 : 20,
    maxWidth: 800,
    margin: '0 auto',
  }),
  intentCard: (active, accentColor) => ({
    borderRadius: 20,
    padding: '28px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    border: active ? `2px solid ${accentColor}` : '2px solid #eef0f4',
    backgroundColor: active ? `${accentColor}08` : '#fff',
    boxShadow: active ? `0 8px 30px ${accentColor}15` : 'none',
  }),
  intentIcon: (active, accentColor) => ({
    width: 56,
    height: 56,
    borderRadius: 16,
    margin: '0 auto 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: active ? accentColor : '#f0f2f5',
    color: active ? '#fff' : colors.muted,
    fontSize: 28,
    transition: 'all 0.25s ease',
  }),
  intentTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 6,
  },
  intentDesc: {fontSize: 13, color: colors.muted, lineHeight: 1.4},
  actionSection: sm => ({
    background: colors.surface,
    padding: sm ? '40px 12px' : '60px 24px',
    boxSizing: 'border-box',
    width: '100%',
    overflowX: 'hidden',
  }),
  storeBtn: sm => ({
    width: sm ? '100%' : 240,
    height: 64,
    borderRadius: 16,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 15,
  }),
  authCard: sm => ({
    background: '#fff',
    borderRadius: sm ? 16 : 24,
    padding: sm ? '24px 16px' : '32px 28px',
    maxWidth: 440,
    width: '100%',
    margin: '0 auto',
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    boxSizing: 'border-box',
  }),
  authToggleRow: {
    display: 'flex',
    borderRadius: 12,
    background: '#f4f6fa',
    padding: 4,
    marginBottom: 24,
  },
  authToggleBtn: active => ({
    flex: 1,
    padding: '9px 0',
    border: 'none',
    borderRadius: 9,
    background: active ? '#fff' : 'transparent',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 14,
    color: active ? colors.azul : colors.muted,
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
  }),
  socialBtn: (bg, textColor) => ({
    width: '100%',
    height: 50,
    borderRadius: 14,
    border: `1px solid ${bg === '#fff' ? '#e0e0e0' : bg}`,
    marginBottom: 10,
    background: bg,
    color: textColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 15,
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    transition: 'background 0.15s ease',
  }),
  successCard: sm => ({
    background: '#fff',
    borderRadius: sm ? 16 : 24,
    padding: sm ? '28px 16px' : '40px 28px',
    maxWidth: 440,
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    boxSizing: 'border-box',
  }),
  nav: {
    background: colors.azul,
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'fixed',
    zIndex: 9999,
    top: 0,
    left: 0,
    width: '100%',
  },
};

// ─── Intent options ─────────────────────────────────────────────────────────

const INTENTS = [
  {
    key: 'client',
    title: 'Quero ser cliente',
    desc: 'Pedir delivery, mototáxi ou entregas pelo app',
    emoji: '🛵',
    color: colors.azul,
    action: 'download',
  },
  {
    key: 'mme',
    title: 'Quero trabalhar',
    desc: 'Ser motorista, mototaxista ou entregador',
    emoji: '💼',
    color: colors.roxo,
    action: 'download',
  },
  {
    key: 'store',
    title: 'Cadastrar minha loja',
    desc: 'Vender pelo app com a logística Irê it',
    emoji: '🏪',
    color: colors.laranja,
    action: 'register',
  },
];

// ─── Track helper ───────────────────────────────────────────────────────────

function trackAccess(params) {
  const src = params.code || params.ref || `indicacao_${Date.now()}`;
  fetch(
    `https://ireit.com.br/track.php?src=${encodeURIComponent(
      src,
    )}&mode=indicacao&cid=${encodeURIComponent(
      params.cid || '',
    )}&ref=${encodeURIComponent(params.ref || '')}`,
  ).catch(() => {});
}

// ─── Page Component ─────────────────────────────────────────────────────────

const Indicacao = () => {
  const firebaseAuth = getAuth();
  const sm = useMediaQuery({query: '(max-width: 750.98px)'});
  const vsm = useMediaQuery({query: '(max-width: 450px)'});
  const [searchParams] = useSearchParams();
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState('auth');
  const [authUser, setAuthUser] = useState(null);
  const [existingAccounts] = useState(null);
  const [checkingAccounts] = useState(false);
  const [intent, setIntent] = useState(null);
  const [authMode, setAuthMode] = useState('register');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [referredUserName, setReferredUserName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // Parse URL params
  const params = useMemo(
    () => ({
      code: searchParams.get('code') || '',
      src: searchParams.get('src') || 'client',
      cid: searchParams.get('cid') || '',
      ref: searchParams.get('ref') || '',
    }),
    [searchParams],
  );

  // Track on mount
  useEffect(() => {
    if (params.code) trackAccess(params);
  }, [params]);

  // Save referral data to localStorage (app reads on first login)
  useEffect(() => {
    if (params.code) {
      localStorage.setItem(
        'ireit_referral',
        JSON.stringify({
          code: params.code,
          src: params.src,
          cid: params.cid,
          ref: params.ref,
          savedAt: Date.now(),
        }),
      );
    }
  }, [params]);

  // Scroll to top
  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  // Fab visibility
  useEffect(() => {
    const toggle = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', toggle);
    return () => window.removeEventListener('scroll', toggle);
  }, []);

  // Always sign out on page load so each referral flow starts fresh.
  // This handles the case where someone used a different account before.
  // After a successful referral the user stays logged in until the next reload.
  useEffect(() => {
    signOut(firebaseAuth).catch(() => {});
  }, []); // intentionally empty: run once on mount only

  // ─── Auth handlers ────────────────────────────────────────────────────────

  const processAfterAuth = async firebaseUser => {
    setAuthUser(firebaseUser);
    setAuthLoading(false);
    setStep('intent');
  };

  const handleEmailAuth = async () => {
    const emailVal = authEmail.trim();
    if (!emailVal) return setAuthError('Email obrigatório');
    if (!authPassword) return setAuthError('Senha obrigatória');
    if (authMode === 'register') {
      if (authPassword.length < 6)
        return setAuthError('Senha deve ter no mínimo 6 caracteres');
    }
    setAuthError('');
    setAuthLoading(true);
    try {
      let cred;
      if (authMode === 'register') {
        cred = await createUserWithEmailAndPassword(
          firebaseAuth,
          emailVal,
          authPassword,
        );
        await updateProfile(cred.user, {
          displayName: referredUserName.trim() || authEmail.split('@')[0],
        });
      } else {
        cred = await signInWithEmailAndPassword(
          firebaseAuth,
          emailVal,
          authPassword,
        );
      }
      await processAfterAuth(cred.user);
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'Email já cadastrado. Faça login.',
        'auth/wrong-password': 'Senha incorreta.',
        'auth/user-not-found': 'Usuário não encontrado.',
        'auth/invalid-email': 'Email inválido.',
        'auth/too-many-requests':
          'Muitas tentativas. Tente novamente mais tarde.',
        'auth/weak-password': 'Use ao menos 6 caracteres na senha.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
      };
      setAuthError(msgs[err.code] || 'Erro ao autenticar. Tente novamente.');
      setAuthLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);
      await processAfterAuth(result.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setAuthError('Erro ao entrar com Google.');
      }
      setAuthLoading(false);
    }
  };

  const handleAppleAuth = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(firebaseAuth, provider);
      await processAfterAuth(result.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setAuthError('Erro ao entrar com Apple.');
      }
      setAuthLoading(false);
    }
  };

  const handleIntentSelect = async selectedIntent => {
    setIntent(selectedIntent);
    setAuthLoading(true);
    setAuthError('');

    try {
      const token = await authUser.getIdToken();
      const uid = authUser.uid;
      const cdg = params.code;
      let profileRes = null;

      // ── Passo 1: salva o código no perfil do usuário ──────────────────────
      if (selectedIntent === 'client') {
        profileRes = await fetch(
          `${API_BASE}/users/backup/${uid}/cdgindc?cdginc=${cdg}&cpnh=${params.cid}`,
          {headers: {Authorization: `Bearer ${token}`}},
        );
      } else if (selectedIntent === 'mme') {
        profileRes = await fetch(
          `${API_BASE}/parceiros/get/user/indcdg?userid=${uid}&indcdg=${cdg}&cpnh=${params.cid}`,
          {headers: {Authorization: `Bearer ${token}`}},
        );
      }
      // store vai direto para done sem chamar endpoint de perfil aqui

      // Verifica conflito de código
      if (profileRes && profileRes.ok) {
        const data = await profileRes.json();

        // client → BackupModel: indcdg está dentro de users[0] ou user
        // mme    → Parceirodadosmodels: indcdg está na raiz do objeto
        const returnedCode =
          selectedIntent === 'client'
            ? data.users?.[0]?.indcdg ?? data.user?.indcdg ?? ''
            : data.indcdg ?? '';

        if (returnedCode && returnedCode !== params.code) {
          setAuthError(
            `Essa conta já está vinculada ao código ${returnedCode}. Não é possível usar outro código.`,
          );
          setIntent(null);
          return;
        }
      }

      // ── Passo 2: registra a indicação no microserviço de cupons ───────────
      // Só executa se temos cid e ref (link veio de uma campanha válida)
      if (params.cid && params.ref && params.code) {
        try {
          const profileMap = {client: 'client', mme: 'driver', store: 'store'};
          const qs = new URLSearchParams({
            campaignConfigId: params.cid,
            referralCode: params.code,
            referredUserId: authUser.uid,
            referredUserProfile: profileMap[selectedIntent] || selectedIntent,
            referrerUserId: params.ref,
            referredUserName: referredUserName.trim(),
          });
          // Não bloqueia o fluxo — se falhar o usuário já foi cadastrado
          fetch(`${API_BASE}/cupons/referral/progress/register?${qs}`, {
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`},
          }).catch(() => {
            // fallback silencioso — o progress pode ser criado retroativamente
            console.warn('[referral] falha ao registrar progress, seguindo...');
          });
        } catch {
          // idem — não bloqueia
        }
      }

      setStep('done');
    } catch {
      setStep('done');
    } finally {
      setAuthLoading(false);
    }
  };

  const srcLabel =
    {
      client: 'um amigo',
      mme: 'um parceiro',
      store: 'uma loja',
      influencer: 'um influenciador',
    }[params.src] || 'alguém';

  return (
    <div style={s.page}>
      {/* Scroll to top fab */}
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 2}}
          size="small"
          color="primary"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* ── Navbar ── */}
      <div style={s.nav}>
        <Button
          component={Link}
          to="/"
          style={{
            width: 95,
            height: 47,
            marginLeft: 12,
            backgroundImage: `url(${logo})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
        {(sm || vsm) && (
          <MenuIcon
            style={{color: '#fff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        )}
        {!sm && !vsm && <Tab />}
      </div>
      {(sm || vsm) && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      {/* ══════════ HERO ══════════ */}
      <div style={s.hero(sm)}>
        <div style={s.heroGlow} />
        <div style={s.heroGlow2} />

        <SlideUp delay="0.1s">
          <div style={s.codeBox(sm)}>
            {params.code ? (
              <>
                <p style={s.codeLabel}>Código de indicação</p>
                <CodeValue style={s.codeValue(sm)}>{params.code}</CodeValue>
              </>
            ) : (
              <>
                <p style={{...s.codeLabel, color: 'rgba(255,150,150,0.8)'}}>
                  Nenhum código encontrado
                </p>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 14,
                    marginTop: 8,
                    marginBottom: 4,
                  }}
                >
                  Peça o link de indicação pro seu amigo
                </p>
              </>
            )}
            <Button
              style={{
                ...s.copyBtn,
                marginTop: 12,
                paddingLeft: 24,
                paddingRight: 24,
              }}
              onClick={() =>
                document
                  .getElementById('main-flow')
                  ?.scrollIntoView({behavior: 'smooth'})
              }
            >
              Continuar →
            </Button>
          </div>
        </SlideUp>

        <SlideUp delay="0.2s">
          <p style={s.title(sm)}>
            Você foi indicado por {srcLabel} para a{' '}
            <span style={{color: colors.laranja}}>Irê it</span>
          </p>
        </SlideUp>
        <SlideUp delay="0.3s">
          <p style={s.subtitle(sm)}>
            Cadastre-se e aproveite os benefícios. Escolha abaixo como quer usar
            a plataforma.
          </p>
        </SlideUp>
      </div>

      {/* ══════════ MAIN FLOW ══════════ */}
      <div id="main-flow" style={s.actionSection(sm)}>
        {/* STEP 1 — Auth */}
        {step === 'auth' && (
          <SlideUp delay="0.1s">
            <div style={s.authCard(sm)}>
              {params.code && (
                <div
                  style={{
                    background: `${colors.azul}08`,
                    border: `1px solid ${colors.azul}20`,
                    borderRadius: 12,
                    padding: '10px 16px',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                  }}
                >
                  <span style={{fontSize: 12, color: colors.muted}}>
                    Código de indicação:
                  </span>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: colors.azul,
                      fontFamily: 'monospace',
                      letterSpacing: 2,
                    }}
                  >
                    {params.code}
                  </span>
                </div>
              )}
              <p
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: colors.text,
                  textAlign: 'center',
                  marginBottom: 4,
                }}
              >
                Crie sua conta ou entre
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: colors.muted,
                  textAlign: 'center',
                  marginBottom: 20,
                }}
              >
                Depois você escolhe como usar a Irê it
              </p>

              {/* Login / Register toggle */}
              <div style={s.authToggleRow}>
                <button
                  style={s.authToggleBtn(authMode === 'register')}
                  onClick={() => {
                    setAuthMode('register');
                    setAuthError('');
                  }}
                >
                  Criar conta
                </button>
                <button
                  style={s.authToggleBtn(authMode === 'login')}
                  onClick={() => {
                    setAuthMode('login');
                    setAuthError('');
                  }}
                >
                  Já tenho conta
                </button>
              </div>

              {/* Campaign display name (always shown) */}
              <TextField
                label="Digite seu nome..."
                variant="outlined"
                size="small"
                fullWidth
                style={{marginBottom: 14}}
                value={referredUserName}
                onChange={e => setReferredUserName(e.target.value)}
                disabled={authLoading}
                autoComplete="nickname"
                helperText="Nome exibido para quem te indicou acompanhar o progresso"
              />

              {/* Email */}
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                size="small"
                fullWidth
                style={{marginBottom: 14}}
                value={authEmail}
                onChange={e => setAuthEmail(e.target.value)}
                disabled={authLoading}
                autoComplete="email"
              />

              {/* Password */}
              <TextField
                label="Senha"
                variant="outlined"
                size="small"
                fullWidth
                style={{marginBottom: 6}}
                type={showPass ? 'text' : 'password'}
                value={authPassword}
                onChange={e => setAuthPassword(e.target.value)}
                disabled={authLoading}
                autoComplete={
                  authMode === 'register' ? 'new-password' : 'current-password'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPass(v => !v)}
                        edge="end"
                      >
                        {showPass ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailAuth();
                }}
              />

              {/* Error */}
              {authError && (
                <p
                  style={{
                    color: '#e53935',
                    fontSize: 13,
                    marginBottom: 8,
                    marginTop: 4,
                    textAlign: 'center',
                  }}
                >
                  {authError}
                </p>
              )}

              {/* Main CTA */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleEmailAuth}
                disabled={authLoading}
                sx={{
                  bgcolor: colors.azul,
                  ':hover': {bgcolor: '#053670'},
                  borderRadius: '14px',
                  height: 52,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  mt: '8px',
                  mb: '4px',
                }}
              >
                {authLoading ? (
                  <CircularProgress size={22} style={{color: '#fff'}} />
                ) : authMode === 'register' ? (
                  'Criar conta'
                ) : (
                  'Entrar'
                )}
              </Button>

              <Divider style={{margin: '16px 0'}}>
                <span
                  style={{
                    fontSize: 12,
                    color: colors.muted,
                    padding: '0 8px',
                  }}
                >
                  ou continuar com
                </span>
              </Divider>

              {/* Google */}
              <button
                style={{
                  ...s.socialBtn('#fff', '#3c4043'),
                  opacity: referredUserName.trim().length < 4 ? 0.4 : 1,
                  cursor:
                    referredUserName.trim().length < 4
                      ? 'not-allowed'
                      : 'pointer',
                }}
                onClick={handleGoogleAuth}
                disabled={authLoading || referredUserName.trim().length < 4}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f8f9fa')
                }
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar com Google
              </button>

              {/* Apple */}
              <button
                style={{
                  ...s.socialBtn('#000', '#fff'),
                  marginBottom: 0,
                  opacity: referredUserName.trim().length < 4 ? 0.4 : 1,
                  cursor:
                    referredUserName.trim().length < 4
                      ? 'not-allowed'
                      : 'pointer',
                }}
                onClick={handleAppleAuth}
                disabled={authLoading || referredUserName.trim().length < 4}
                onMouseEnter={e => (e.currentTarget.style.background = '#222')}
                onMouseLeave={e => (e.currentTarget.style.background = '#000')}
              >
                <Apple style={{fontSize: 20}} />
                Continuar com Apple
              </button>
            </div>
          </SlideUp>
        )}

        {/* STEP 2 — Intent */}
        {step === 'intent' && (
          <SlideUp delay="0.1s">
            {checkingAccounts ? (
              <div style={{textAlign: 'center', padding: '40px 0'}}>
                <CircularProgress size={32} style={{color: colors.azul}} />
                <p style={{color: colors.muted, marginTop: 12, fontSize: 14}}>
                  Verificando suas contas...
                </p>
              </div>
            ) : (
              <div style={{maxWidth: 800, margin: '0 auto'}}>
                <p style={{...s.sectionTitle(sm), marginBottom: 6}}>
                  Como você quer usar a Irê it?
                </p>
                <p style={{...s.sectionSub, marginBottom: 28}}>
                  Escolha uma opção para continuar
                </p>
                {authError && (
                  <p
                    style={{
                      color: '#e53935',
                      fontSize: 13,
                      textAlign: 'center',
                      marginBottom: 20,
                      background: '#fdecea',
                      borderRadius: 10,
                      padding: '10px 16px',
                    }}
                  >
                    {authError}
                  </p>
                )}
                <div style={s.intentGrid(sm)}>
                  {INTENTS.map((it, i) => {
                    const alreadyHas = existingAccounts?.[it.key];
                    return (
                      <SlideUp key={it.key} delay={`${0.1 + i * 0.08}s`}>
                        <div
                          style={{
                            ...s.intentCard(intent === it.key, it.color),
                            opacity: authLoading ? 0.6 : 1,
                            pointerEvents: authLoading ? 'none' : 'auto',
                          }}
                          onClick={() => handleIntentSelect(it.key)}
                          onMouseEnter={e => {
                            if (intent !== it.key)
                              e.currentTarget.style.borderColor =
                                it.color + '40';
                          }}
                          onMouseLeave={e => {
                            if (intent !== it.key)
                              e.currentTarget.style.borderColor = '#eef0f4';
                          }}
                        >
                          <div
                            style={s.intentIcon(intent === it.key, it.color)}
                          >
                            <span style={{fontSize: 28}}>{it.emoji}</span>
                          </div>
                          <p style={s.intentTitle}>{it.title}</p>
                          <p style={s.intentDesc}>{it.desc}</p>
                          {alreadyHas && (
                            <span
                              style={{
                                fontSize: 11,
                                color: it.color,
                                fontWeight: 600,
                                background: `${it.color}15`,
                                borderRadius: 20,
                                padding: '3px 10px',
                                marginTop: 8,
                                display: 'inline-block',
                              }}
                            >
                              Conta existente
                            </span>
                          )}
                          {authLoading && intent === it.key && (
                            <CircularProgress
                              size={18}
                              style={{color: it.color, marginTop: 8}}
                            />
                          )}
                        </div>
                      </SlideUp>
                    );
                  })}
                </div>
                <div style={{textAlign: 'center', marginTop: 24}}>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.muted,
                      fontSize: 13,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => {
                      setStep('auth');
                      setAuthError('');
                    }}
                  >
                    ← Voltar
                  </button>
                </div>
              </div>
            )}
          </SlideUp>
        )}

        {/* STEP 3 — Done */}
        {step === 'done' && (
          <SlideUp delay="0.1s">
            <div style={s.successCard(sm)}>
              <CheckCircleOutline
                style={{
                  fontSize: 64,
                  color: colors.success,
                  marginBottom: 12,
                }}
              />
              <p
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: colors.text,
                  marginBottom: 6,
                }}
              >
                {existingAccounts?.[intent]
                  ? 'Código aplicado na conta existente!'
                  : authMode === 'register'
                  ? 'Conta criada com sucesso!'
                  : 'Login realizado com sucesso!'}
              </p>
              {intent === 'store' ? (
                <>
                  <p
                    style={{
                      fontSize: 14,
                      color: colors.muted,
                      marginBottom: 28,
                    }}
                  >
                    Agora complete o cadastro da sua loja na plataforma Irê it.
                  </p>
                  <Button
                    component={Link}
                    to={`/estabelecimento?referral_code=${encodeURIComponent(
                      params.code,
                    )}&referral_ref=${encodeURIComponent(
                      params.ref,
                    )}&referral_cid=${encodeURIComponent(params.cid)}`}
                    startIcon={<StorefrontOutlined />}
                    sx={{
                      color: '#fff',
                      bgcolor: colors.laranja,
                      ':hover': {bgcolor: '#e55a00'},
                      borderRadius: '14px',
                      height: 52,
                      px: 4,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    Cadastrar estabelecimento
                  </Button>
                  <p style={{fontSize: 12, color: colors.muted, marginTop: 16}}>
                    Sem mensalidade. Taxas a partir de 8%.
                  </p>
                </>
              ) : intent === 'mme' ? (
                <>
                  <p
                    style={{
                      fontSize: 14,
                      color: colors.muted,
                      marginBottom: 8,
                    }}
                  >
                    {params.code
                      ? `Código ${params.code} vinculado.`
                      : 'Cadastro iniciado.'}{' '}
                    Agora complete seu cadastro como parceiro motorista /
                    mototaxista / entregador.
                  </p>

                  {/* Instrução de acesso */}
                  <div
                    style={{
                      background: `${colors.roxo}08`,
                      border: `1px solid ${colors.roxo}25`,
                      borderRadius: 14,
                      padding: '14px 18px',
                      marginBottom: 24,
                      textAlign: 'left',
                    }}
                  >
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: colors.roxo,
                        marginBottom: 6,
                      }}
                    >
                      Como acessar o formulário:
                    </p>
                    <ol
                      style={{
                        fontSize: 13,
                        color: colors.text,
                        paddingLeft: 18,
                        margin: 0,
                        lineHeight: 1.8,
                      }}
                    >
                      <li>
                        Clique em{' '}
                        <strong>&quot;Ir para o cadastro&quot;</strong> abaixo
                      </li>
                      <li>
                        Faça login com o e-mail e senha que você acabou de criar
                      </li>
                      <li>Preencha o formulário de cadastro completo</li>
                    </ol>
                  </div>

                  {/* CTA principal → /cadastro_mme */}
                  <Button
                    component={Link}
                    to="/cadastro_mme"
                    fullWidth
                    sx={{
                      color: '#fff',
                      bgcolor: colors.roxo,
                      ':hover': {bgcolor: '#6a45cc'},
                      borderRadius: '14px',
                      height: 52,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 16,
                      mb: '20px',
                    }}
                  >
                    💼 Ir para o cadastro
                  </Button>

                  <Divider style={{marginBottom: 20}}>
                    <span style={{fontSize: 12, color: colors.muted}}>
                      ou prefiro fazer depois
                    </span>
                  </Divider>

                  <p
                    style={{
                      fontSize: 13,
                      color: colors.muted,
                      marginBottom: 14,
                    }}
                  >
                    Baixe o app Irê it MME e faça o cadastro quando quiser:
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      startIcon={<Android />}
                      style={s.storeBtn(sm)}
                      sx={{
                        color: '#fff',
                        bgcolor: colors.azul,
                        ':hover': {bgcolor: '#053670'},
                      }}
                      target="_blank"
                      href="https://play.google.com/store/apps/details?id=com.ireitmme&hl=pt"
                    >
                      <div style={{marginLeft: 8, textAlign: 'left'}}>
                        <span
                          style={{fontSize: 11, opacity: 0.7, display: 'block'}}
                        >
                          Disponível na
                        </span>
                        <span
                          style={{
                            fontSize: 17,
                            display: 'block',
                            lineHeight: 1.1,
                          }}
                        >
                          Play Store
                        </span>
                      </div>
                    </Button>
                    <Button
                      startIcon={<Apple />}
                      style={s.storeBtn(sm)}
                      sx={{
                        color: colors.azul,
                        bgcolor: '#fff',
                        border: `1px solid ${colors.azul}20`,
                        ':hover': {bgcolor: '#f0f4fa'},
                      }}
                      target="_blank"
                      href="https://apps.apple.com/us/app/irê-it-mme/id6476141498"
                    >
                      <div style={{marginLeft: 8, textAlign: 'left'}}>
                        <span
                          style={{fontSize: 11, opacity: 0.5, display: 'block'}}
                        >
                          Disponível na
                        </span>
                        <span
                          style={{
                            fontSize: 17,
                            display: 'block',
                            lineHeight: 1.1,
                          }}
                        >
                          App Store
                        </span>
                      </div>
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p
                    style={{
                      fontSize: 14,
                      color: colors.muted,
                      marginBottom: 24,
                    }}
                  >
                    {params.code
                      ? `Código ${params.code} vinculado. Baixe o app Irê it para começar:`
                      : 'Baixe o app Irê it para começar:'}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      startIcon={<Android />}
                      style={s.storeBtn(sm)}
                      sx={{
                        color: '#fff',
                        bgcolor: colors.azul,
                        ':hover': {bgcolor: '#053670'},
                      }}
                      target="_blank"
                      href="https://play.google.com/store/apps/details?id=com.ireit&hl=pt"
                    >
                      <div style={{marginLeft: 8, textAlign: 'left'}}>
                        <span
                          style={{fontSize: 11, opacity: 0.7, display: 'block'}}
                        >
                          Disponível na
                        </span>
                        <span
                          style={{
                            fontSize: 17,
                            display: 'block',
                            lineHeight: 1.1,
                          }}
                        >
                          Play Store
                        </span>
                      </div>
                    </Button>
                    <Button
                      startIcon={<Apple />}
                      style={s.storeBtn(sm)}
                      sx={{
                        color: colors.azul,
                        bgcolor: '#fff',
                        border: `1px solid ${colors.azul}20`,
                        ':hover': {bgcolor: '#f0f4fa'},
                      }}
                      target="_blank"
                      href="https://apps.apple.com/br/app/ir%C3%AA-it/id6475050868"
                    >
                      <div style={{marginLeft: 8, textAlign: 'left'}}>
                        <span
                          style={{fontSize: 11, opacity: 0.5, display: 'block'}}
                        >
                          Disponível na
                        </span>
                        <span
                          style={{
                            fontSize: 17,
                            display: 'block',
                            lineHeight: 1.1,
                          }}
                        >
                          App Store
                        </span>
                      </div>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </SlideUp>
        )}
      </div>

      {/* ══════════ INFO SECTION ══════════ */}
      <div style={{...s.section(sm), textAlign: 'center'}}>
        <SlideUp delay="0.1s">
          <p style={{...s.sectionTitle(sm), fontSize: sm ? 20 : 26}}>
            Como funciona a indicação?
          </p>
        </SlideUp>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: sm ? '1fr' : 'repeat(3, 1fr)',
            gap: 20,
            marginTop: 24,
          }}
        >
          {[
            {
              step: '1',
              title: 'Cadastre-se',
              desc: 'Escolha como quer usar a Irê it e crie sua conta',
              color: colors.azul,
            },
            {
              step: '2',
              title: 'Use o serviço',
              desc: 'Faça pedidos, corridas ou cadastre sua loja',
              color: colors.roxo,
            },
            {
              step: '3',
              title: 'Ambos ganham',
              desc: 'Quando você completar a meta, o prêmio é liberado',
              color: colors.laranja,
            },
          ].map((item, i) => (
            <SlideUp key={i} delay={`${0.15 + i * 0.1}s`}>
              <div
                style={{
                  padding: '24px 20px',
                  borderRadius: 16,
                  border: '1px solid #eef0f4',
                  backgroundColor: '#fff',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    margin: '0 auto 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: item.color + '12',
                    color: item.color,
                    fontSize: 18,
                    fontWeight: 800,
                  }}
                >
                  {item.step}
                </div>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: colors.text,
                    marginBottom: 4,
                  }}
                >
                  {item.title}
                </p>
                <p style={{fontSize: 13, color: colors.muted, lineHeight: 1.4}}>
                  {item.desc}
                </p>
              </div>
            </SlideUp>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Indicacao;
